'use client'

const REQUEST_PROOF_HEADER = 'X-TLS-Request-Proof'
const REQUEST_PROOF_CHALLENGE_HEADER = 'X-TLS-Request-Challenge'
const REQUEST_PROOF_CACHE_MS = 10 * 60 * 1000
// The proof is bound to the HttpOnly identity cookie, so it is worthless on
// its own and already readable by page script; keeping it in sessionStorage
// weakens nothing. Module memory alone was reset by every hard page load, so
// each load paid the two-request handshake (~23K function invocations a day).
const REQUEST_PROOF_STORAGE_KEY = 'tls_request_proof'

let cachedProof: string | null = null
let cachedAt = 0
let inFlightProofRequest: Promise<string> | null = null

// Storage can be missing or throw (private mode, blocked site data, server
// render); every failure falls back to the in-memory cache.
function readStoredProof(): { proof: string; at: number } | null {
  try {
    const raw = window.sessionStorage.getItem(REQUEST_PROOF_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as { proof?: unknown }).proof === 'string' &&
      typeof (parsed as { at?: unknown }).at === 'number'
    ) {
      return parsed as { proof: string; at: number }
    }
  } catch {
    // fall through
  }
  return null
}

function storeProof(proof: string, at: number): void {
  try {
    window.sessionStorage.setItem(REQUEST_PROOF_STORAGE_KEY, JSON.stringify({ proof, at }))
  } catch {
    // in-memory cache still applies
  }
}

async function fetchChallenge(): Promise<string> {
  const response = await fetch('/api/challenge', {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin',
  })

  const body = await response.json().catch(() => null)
  const challenge = body?.challenge

  if (!response.ok || typeof challenge !== 'string' || challenge.length === 0) {
    throw new Error(body?.error || 'Unable to obtain request challenge')
  }

  return challenge
}

async function fetchRequestProof(): Promise<string> {
  const challenge = await fetchChallenge()

  const response = await fetch('/api/request-proof', {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      [REQUEST_PROOF_CHALLENGE_HEADER]: challenge,
    },
  })

  const body = await response.json().catch(() => null)
  const proof = body?.proof

  if (!response.ok || typeof proof !== 'string' || proof.length === 0) {
    throw new Error(body?.error || 'Unable to verify request identity')
  }

  cachedProof = proof
  cachedAt = Date.now()
  storeProof(proof, cachedAt)
  return proof
}

export async function getRequestProof(forceRefresh = false): Promise<string> {
  if (!cachedProof) {
    const stored = readStoredProof()
    if (stored) {
      cachedProof = stored.proof
      cachedAt = stored.at
    }
  }

  const isCacheFresh = cachedProof && Date.now() - cachedAt < REQUEST_PROOF_CACHE_MS
  if (!forceRefresh && isCacheFresh && cachedProof) {
    return cachedProof
  }

  if (!inFlightProofRequest) {
    inFlightProofRequest = fetchRequestProof().finally(() => {
      inFlightProofRequest = null
    })
  }

  return inFlightProofRequest
}

export async function withRequestProofHeaders(headers?: HeadersInit): Promise<Headers> {
  const nextHeaders = new Headers(headers)
  nextHeaders.set(REQUEST_PROOF_HEADER, await getRequestProof())
  return nextHeaders
}
